import type { HuntingView } from '@wyn/contracts';
import { TICK_MS } from '@wyn/game-core';
import { useEffect, useRef, useState } from 'react';
import type { TurnGameProps } from './registry.tsx';

/**
 * The Royal Chase. Scout spots for tracks and rustling (the quarry stays on the server), then strike
 * as the ring closes on the target: the tick you press at is sent and judged there.
 */
export function HuntingGame({ view, busy, onMove }: TurnGameProps<HuntingView>) {
  // A strike the server turned down (a desync) remounts the ring so the player can try again.
  const [attempt, setAttempt] = useState(0);
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) setAttempt((n) => n + 1);
    wasBusy.current = busy;
  }, [busy]);
  const quarry = view.bird ? '🐇' : '🦌';
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        Tap a spot to scout it (one turn). 🐾 tracks show which way the quarry went, dim if old. 🌿 rustling means it is right next to you. The quarry moves after every scout.
      </p>
      <div className="flex justify-between font-mono text-sm">
        <span>Turns left <b>{view.turnsLeft}</b></span>
        {view.last && <span role="status" className="text-ink-dim">{view.last}</span>}
      </div>
      <div className="mx-auto grid gap-1" style={{ gridTemplateColumns: `repeat(${view.size}, 1fr)`, maxWidth: view.size * 46 }}>
        {view.cells.map((c, i) => {
          const isPrey = view.prey === i;
          const label = c === null ? `Spot ${i + 1}` : `Scouted: ${c.kind}`;
          return (
            <button
              key={i}
              type="button"
              aria-label={label}
              disabled={busy || view.over || view.phase === 'strike'}
              onClick={() => onMove({ type: 'scout', cell: i })}
              className={`aspect-square border text-sm ${c === null ? 'border-rule bg-scene-grass hover:border-gilt disabled:hover:border-rule' : 'border-rule bg-[#1e2818]'} ${isPrey ? 'border-gilt' : ''}`}
            >
              {c?.kind === 'quarry' || isPrey ? quarry
                : c?.kind === 'tracks' ? <span style={{ opacity: c.fresh ? 1 : 0.45 }}>🐾{c.dir ?? ''}</span>
                : c?.kind === 'rustle' ? '🌿'
                : c?.kind === 'empty' ? <span className="text-ink-faint">·</span>
                : ''}
            </button>
          );
        })}
      </div>
      {view.phase === 'strike' && !view.over && <Strike key={`${view.turnsLeft}-${attempt}`} view={view} busy={busy} onStrike={(tick) => onMove({ type: 'strike', tick })} />}
    </div>
  );
}

/** The ring closes on the target ring; press as they meet. */
function Strike({ view, busy, onStrike }: { view: HuntingView; busy: boolean; onStrike: (tick: number) => void }) {
  const { ring, speed } = view;
  const [r, setR] = useState<number>(ring.start);
  const start = useRef(performance.now());
  const sent = useRef(false);
  const strike = () => {
    if (sent.current) return;
    sent.current = true;
    onStrike(Math.floor((performance.now() - start.current) / TICK_MS));
  };
  const strikeRef = useRef(strike);
  strikeRef.current = strike;

  useEffect(() => {
    let raf = 0;
    const tick = (now: number) => {
      const radius = ring.start - ring.rate * speed * ((now - start.current) / 1000);
      setR(Math.max(0, radius));
      if (radius <= 0) return strikeRef.current();
      if (!sent.current) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const key = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      e.preventDefault();
      strikeRef.current();
    };
    addEventListener('keydown', key);
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('keydown', key);
    };
  }, [ring, speed]);

  return (
    <div className="space-y-3">
      <div className="relative mx-auto h-44" aria-label="Strike ring">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-verdigris" style={{ width: ring.target, height: ring.target }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-gilt" style={{ width: r, height: r }} />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl">{view.bird ? '🐇' : '🦌'}</div>
      </div>
      <button type="button" className="w-full border-2 border-gilt/70 py-4 text-lg text-gilt" disabled={busy} onPointerDown={(e) => { e.preventDefault(); strike(); }}>
        {view.bird ? '🦅 Release the falcon' : '🏹 Loose!'}
      </button>
    </div>
  );
}
